import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useWalletAlerts, { isUserLogin } from "./useWalletAlerts";

const POPOVER_ID = "wallet-alerts-popover";
const SEVERITY_COLORS = { critical: "#B42318", warning: "#B54708" };

// What an empty bell says, written for the login that is looking at it.
const emptyHint = (enabled, userType) => {
  if (!enabled) return "Nothing new.";
  return isUserLogin(userType)
    ? "Set a minimum balance on your Policy Wallet page to be warned here when it runs low."
    : "Set a minimum on a wallet card to be warned here when it runs low.";
};

/**
 * The navbar bell. Shows how many wallets are at or below the minimum their owner set (admin: the insurer wallets,
 * user: their own Policy Wallet) and lists them; each one opens the wallet to fix it. The server is asked only when
 * the bell is clicked, and works the list out on every look, so there is nothing to mark as read: an alert goes away
 * when the balance is topped up or the minimum is changed.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const userType = sessionStorage.getItem("userType");
  const { enabled, alerts, count, loaded, loading, error, refresh } = useWalletAlerts(userType);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    refresh(); // the only place the server is asked: when the person clicks the bell
  };
  const handleClose = () => setAnchorEl(null);
  const handleGo = (link) => {
    handleClose();
    navigate(link);
  };

  const countText = `${count} wallet ${count === 1 ? "alert" : "alerts"}`;
  const firstLook = enabled && !loaded && !error; // nothing known yet and nothing failed yet

  return (
    <>
      <Tooltip title={count > 0 ? countText : "Notifications"}>
        <IconButton
          aria-label={count > 0 ? `Notifications, ${countText}` : "Notifications"}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? POPOVER_ID : undefined}
          onClick={handleOpen}
          sx={{ color: "#C4C3C7" }}
        >
          <Badge badgeContent={count} color="error" max={9} invisible={count === 0}>
            <NotificationsActiveIcon sx={{ fontSize: "26px" }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={POPOVER_ID}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ role: "dialog", "aria-label": "Notifications", sx: { mt: 1, borderRadius: "10px" } }}
      >
        <Box sx={{ width: 380, maxWidth: "calc(100vw - 32px)" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #EAECF0" }}>
            <Typography sx={{ fontFamily: "Inter", fontWeight: 600, fontSize: "15px", color: "#1E3A5F" }}>
              Notifications
            </Typography>
          </Box>

          {firstLook && loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2.5 }}>
              <CircularProgress size={18} sx={{ color: "#1E3A5F" }} />
              <Typography sx={{ fontSize: "13px", color: "#667085" }}>Checking your wallets…</Typography>
            </Box>
          )}

          {enabled && !loaded && error && (
            <Box role="alert" sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2 }}>
              <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
              <Typography sx={{ flex: 1, fontSize: "13px", color: "#b3261e" }}>
                Could not check your wallets just now.
              </Typography>
              <Button size="small" variant="outlined" onClick={refresh}>
                Try again
              </Button>
            </Box>
          )}

          {count > 0 && (
            <List disablePadding aria-label="Wallet alerts">
              {alerts.map((alert) => (
                <ListItemButton
                  key={alert.id}
                  alignItems="flex-start"
                  divider
                  onClick={() => handleGo(alert.link)}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                    <WarningAmberIcon sx={{ color: SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.warning }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={alert.title}
                    secondary={
                      <>
                        {alert.message}
                        <Box component="span" sx={{ display: "block", mt: 0.5, fontWeight: 600, color: "#1E3A5F" }}>
                          View wallet
                        </Box>
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}
                    secondaryTypographyProps={{ fontSize: "12.5px", color: "#475467", component: "div" }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          {!firstLook && count === 0 && !(enabled && !loaded && error) && (
            <Box sx={{ px: 2, py: 2.5 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E3A5F" }}>
                You are all caught up
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "12.5px", color: "#667085" }}>
                {emptyHint(enabled, userType)}
              </Typography>
            </Box>
          )}

          {loaded && error && (
            <Typography role="status" sx={{ px: 2, py: 1, fontSize: "12px", color: "#667085" }}>
              Could not refresh just now. Showing the last check.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
